import type { Plugin, PointElement, TooltipModel } from 'chart.js';
import { getCSSVariableValue } from '../../../util';
import { getRainbowLinearGradient, getRainbowVerticalLinearGradient } from '../utils';

export const STOCK_PRICE_DATASET_LABEL = 'Price';

export const STOCK_PRICE_MARKER_RADIUS = 6;

const MARKER_RING_WIDTH = 2;
const MARKER_RING_RADIUS = STOCK_PRICE_MARKER_RADIUS - MARKER_RING_WIDTH / 2;
const MARKER_CORE_RADIUS = STOCK_PRICE_MARKER_RADIUS - MARKER_RING_WIDTH;
const MARKER_SHADOW_BOUNDS = STOCK_PRICE_MARKER_RADIUS * 2;
const MARKER_SHADOW_COLOR = 'rgba(255, 255, 255, 0.5)';
const MARKER_SHADOW_OFFSET_Y = 4;
const MARKER_SHADOW_BLUR = 2;
const GUIDE_LINE_WIDTH = 1;

type StockPriceInspectionTooltipModel = Pick<TooltipModel<'line'>, 'dataPoints' | 'opacity'>;

/** Returns the data index of the inspected Price point, or null when nothing is inspected. */
export function getStockPriceInspectionIndex(tooltip: StockPriceInspectionTooltipModel | undefined): number | null {
  if (!tooltip || tooltip.opacity === 0) {
    return null;
  }

  return tooltip.dataPoints?.find((point) => point.dataset.label === STOCK_PRICE_DATASET_LABEL)?.dataIndex ?? null;
}

function drawMarkerInnerShadow(ctx: CanvasRenderingContext2D, x: number, y: number, shadowScale: number) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, MARKER_CORE_RADIUS, 0, Math.PI * 2);
  ctx.clip();

  ctx.beginPath();
  ctx.rect(x - MARKER_SHADOW_BOUNDS, y - MARKER_SHADOW_BOUNDS, MARKER_SHADOW_BOUNDS * 2, MARKER_SHADOW_BOUNDS * 2);
  ctx.arc(x, y, STOCK_PRICE_MARKER_RADIUS, 0, Math.PI * 2, true);
  ctx.shadowColor = MARKER_SHADOW_COLOR;
  ctx.shadowOffsetY = MARKER_SHADOW_OFFSET_Y * shadowScale;
  ctx.shadowBlur = MARKER_SHADOW_BLUR * shadowScale;
  ctx.fillStyle = '#000000';
  ctx.fill();
  ctx.restore();
}

export function buildStockPriceInspectionPlugin(getSelectedIndex: () => number | null): Plugin<'line'> {
  return {
    id: 'stockPriceInspection',
    afterDatasetsDraw(chart) {
      const dataIndex = getSelectedIndex();

      if (dataIndex === null) {
        return;
      }

      const datasetIndex = chart.data.datasets.findIndex((dataset) => dataset.label === STOCK_PRICE_DATASET_LABEL);

      if (datasetIndex === -1) {
        return;
      }

      const meta = chart.getDatasetMeta(datasetIndex);
      const element = meta.data[dataIndex] as PointElement | undefined;

      if (!meta.visible || !element || element.skip) {
        return;
      }

      const { x, y } = element.getProps(['x', 'y'], true);

      if (x === null || y === null) {
        return;
      }

      const { chartArea, ctx } = chart;

      ctx.save();

      if (y < chartArea.bottom) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = GUIDE_LINE_WIDTH;
        ctx.strokeStyle = getRainbowVerticalLinearGradient(ctx, y, chartArea.bottom);
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(x, y, MARKER_CORE_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = getRainbowLinearGradient(ctx, x - STOCK_PRICE_MARKER_RADIUS, x + STOCK_PRICE_MARKER_RADIUS);
      ctx.fill();

      drawMarkerInnerShadow(ctx, x, y, chart.currentDevicePixelRatio);

      ctx.beginPath();
      ctx.arc(x, y, MARKER_RING_RADIUS, 0, Math.PI * 2);
      ctx.lineWidth = MARKER_RING_WIDTH;
      ctx.strokeStyle = getCSSVariableValue('--ixf-color-background') || '#ffffff';
      ctx.stroke();

      ctx.restore();
    },
  };
}
